// src/common/components/ShowToast.tsx
import { toast } from 'react-hot-toast';
import type { CSSProperties } from 'react';
import { getShowAlerts } from '../../Notification/globalNotifications';

export enum ToastTypeEnum {
  Success = 'success',
  Error = 'error',
  Warn = 'warn',
}

interface ShowToastProps {
  message: string;
  type: ToastTypeEnum;
  duration?: number;
  isWarning?: boolean; // allows “soft warning” styling on success, if needed
}

const toastStyles: Record<ToastTypeEnum, CSSProperties> = {
  [ToastTypeEnum.Success]: {
    borderRadius: '10px',
    border: '1px solid #1A55F9',
    background: '#F2F3FF',
    color: '#131426',
    fontFamily: 'Poppins',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
    letterSpacing: '-0.3px',
    marginTop: '50px',
  },
  [ToastTypeEnum.Error]: {
    borderRadius: '10px',
    border: '1px solid #CC0000',
    background: '#FFD2D2',
    color: '#CC0000',
    fontFamily: 'Poppins',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
    letterSpacing: '-0.3px',
    marginTop: '50px',
  },
  [ToastTypeEnum.Warn]: {
    borderRadius: '10px',
    border: '1px solid #FFA500',
    background: '#FFF4E5',
    color: '#8A4B00',
    fontFamily: 'Poppins',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 'normal',
    letterSpacing: '-0.3px',
    marginTop: '50px',
  },
};

const ShowToast = ({ message, type, duration = 3000, isWarning = false }: ShowToastProps) => {
  // Suppress only non-critical warnings when Show Alerts is OFF
  if ((type === ToastTypeEnum.Warn || isWarning) && !getShowAlerts()) {
    return null;
  }

  const style = toastStyles[type];

  let toastId: string | undefined;
  if (type === ToastTypeEnum.Warn) toastId = 'warn';
  if (type === ToastTypeEnum.Success) toastId = 'success';
  if (type === ToastTypeEnum.Error) toastId = 'error';

  // Dismiss previous toast of the same type before showing a new one
  if (toastId) toast.dismiss(toastId);

  if (type === ToastTypeEnum.Success) {
    toast.success(message, {
      style,
      duration,
      id: toastId, // prevents duplicates
      icon: isWarning ? (
        <img src="/images/warn.svg" alt="Warning" />
      ) : (
        <img src="/images/blueTick.svg" alt="Success" />
      ),
    });
  } else if (type === ToastTypeEnum.Error) {
    toast.error(message, {
      style,
      duration,
      id: toastId,
    });
  } else if (type === ToastTypeEnum.Warn) {
    toast(message, {
      style,
      duration,
      id: toastId,
      icon: <img src="/images/warn.svg" alt="Warning" />,
    });
  }

  return null;
};

export default ShowToast;
